import { useEffect, useRef, useState } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import { Editor } from '@tiptap/react';

type TabType = 'math' | 'physics' | 'chemistry';

const formulaPresets: Record<TabType, { latex: string; label: string }[]> = {
  math: [
    { latex: '\\int_a^b f(x)dx', label: 'Definite Integral' },
    { latex: '\\sum_{i=1}^n i', label: 'Summation' },
    { latex: 'x^2 + y^2 = z^2', label: 'Pythagorean Theorem' },
    { latex: '\\sqrt{a^2 + b^2}', label: 'Square Root' },
    { latex: 'x_i', label: 'Subscript' },
    { latex: '\\frac{a}{b}', label: 'Fraction' },
    { latex: '\\pm', label: 'Plus-minus' },
    { latex: '\\cdot', label: 'Dot product' },
    { latex: '\\leq', label: 'Less than or equal' },
    { latex: '\\geq', label: 'Greater than or equal' },
    { latex: '\\rightarrow', label: 'Right arrow' },
    { latex: '\\leftarrow', label: 'Left arrow' },
    { latex: '\\neq', label: 'Not equal' },
    { latex: '\\approx', label: 'Approximately equal' },
  ],
  physics: [
    { latex: 'F = ma', label: 'Newton’s 2nd Law' },
    { latex: 'E = mc^2', label: 'Mass-Energy' },
    { latex: 'v = u + at', label: 'Equation of Motion' },
    { latex: 'W = F \\cdot d', label: 'Work' },
    { latex: 'P = \\frac{W}{t}', label: 'Power' },
    { latex: 'V = IR', label: 'Ohm’s Law' },
    { latex: 'p = \\frac{mv}{V}', label: 'Pressure' },
    { latex: 'Q = mc\\Delta T', label: 'Heat Energy' },
  ],
  chemistry: [
    { latex: '\\ce{H2 + O2 -> H2O}', label: 'Water Formation (mhchem)' },
    { latex: '\\ce{NaCl ->[H2O] Na+ + Cl-}', label: 'Ionization (mhchem)' },
    { latex: '\\ce{CH4 + 2O2 -> CO2 + 2H2O}', label: 'Combustion' },
    { latex: '\\ce{NH4+ <=> NH3 + H+}', label: 'Equilibrium' },
    { latex: '\\ce{Zn^2+ + 2e- -> Zn}', label: 'Reduction Reaction' },
  ],
};

export const FormulaInsertDialog = ({
  editor,
  onClose,
}: {
  editor: Editor;
  onClose: () => void;
}) => {
  const [input, setInput] = useState('');
  const [tab, setTab] = useState<TabType>('math');
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const insert = () => {
    if (input.trim()) {
      editor.chain().focus().insertMathInline(input.trim()).run();
      onClose();
    }
  };

  const handleSymbolClick = (symbol: string) => {
    setInput((prev) => prev + ' ' + symbol);
  };

  let rendered;
  try {
    rendered = katex.renderToString(input, { throwOnError: false });
  } catch {
    rendered = '<span style="color: red;">Invalid LaTeX</span>';
  }

  const styles = {
    dialogWrapper: {
      position: 'relative' as const,
      top: '0%',
      left: 0,
      marginTop: '8px',
      zIndex: 9999,
    },
    dialog: {
      background: 'white',
      borderRadius: 8,
      boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
      width: 500,
      maxWidth: '95%',
      padding: 20,
      border: '1px solid #ccc',
    },
    header: {
      fontSize: '1.2rem',
      fontWeight: 'bold',
      marginBottom: 10,
    },
    tabs: {
      display: 'flex',
      gap: 10,
      marginBottom: 10,
    },
    tab: (active: boolean) => ({
      padding: '6px 12px',
      borderRadius: 4,
      fontSize: 14,
      cursor: 'pointer',
      backgroundColor: active ? '#007bff' : '#f0f0f0',
      color: active ? '#fff' : '#333',
      border: 'none',
    }),
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: 10,
      maxHeight: 150,
      overflowY: 'auto' as const,
      marginBottom: 10,
    },
    symbolButton: {
      border: '1px solid #ccc',
      padding: 5,
      borderRadius: 4,
      cursor: 'pointer',
      backgroundColor: '#fff',
      textAlign: 'center' as const,
    },
    textarea: {
      width: '100%',
      border: '1px solid #ccc',
      borderRadius: 4,
      padding: 8,
      fontFamily: 'monospace',
      fontSize: 14,
      marginBottom: 10,
    },
    preview: {
      backgroundColor: '#f9f9f9',
      padding: 10,
      borderRadius: 4,
      minHeight: 40,
      textAlign: 'center' as const,
      marginBottom: 10,
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 10,
    },
    button: {
      fontSize: 14,
      padding: '6px 12px',
      borderRadius: 4,
      border: '1px solid #ccc',
      cursor: 'pointer',
    },
    insertButton: {
      backgroundColor: '#007bff',
      color: '#fff',
      border: 'none',
    },
  };

  return (
    <div
      style={styles.dialogWrapper}
      ref={dialogRef}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={styles.dialog}>
        <div style={styles.header}>Insert Formula</div>

        <div style={styles.tabs}>
          {(['math', 'physics', 'chemistry'] as const).map((type) => (
            <button
              key={type}
              onClick={() => setTab(type)}
              style={styles.tab(tab === type)}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        <div style={styles.grid}>
          {formulaPresets[tab].map(({ latex, label }, i) => (
            <button
              key={i}
              style={styles.symbolButton}
              onClick={() => handleSymbolClick(latex)}
              title={label}
              dangerouslySetInnerHTML={{
                __html: katex.renderToString(latex, { throwOnError: false }),
              }}
            />
          ))}
        </div>

        <label style={{ fontSize: 14, marginBottom: 4, display: 'block' }}>
          LaTeX Expression
        </label>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          style={styles.textarea}
          rows={3}
          placeholder="Type or select a formula..."
        />

        <label style={{ fontSize: 14, marginBottom: 4, display: 'block' }}>
          Preview
        </label>
        <div
          style={styles.preview}
          dangerouslySetInnerHTML={{ __html: rendered }}
        />

        <div style={styles.actions}>
          <button style={styles.button} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{ ...styles.button, ...styles.insertButton }}
            onClick={insert}
          >
            Insert
          </button>
        </div>
      </div>
    </div>
  );
};
